import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, ViewChildren, QueryList, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../core/services/auth.service';
import { MfaService } from '../../../core/services/mfa.service';
import { ThemeService } from '../../../core/services/theme.service';
import { WebauthnService } from '../../../core/services/webauthn.service';
import { TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('canvasWrapper') canvasWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild('cardEl')        cardEl!: ElementRef<HTMLDivElement>;
  @ViewChild('flashEl')       flashEl!: ElementRef<HTMLDivElement>;
  @ViewChildren('otpInput')   otpInputs!: QueryList<ElementRef<HTMLInputElement>>;

  loginForm: FormGroup;
  loading       = false;
  showPassword  = false;
  faceIdLoading = false;
  // true when this device has a registered Face ID → show biometric as primary CTA
  hasFaceId     = false;
  // true when user clicks "Usar senha" on the Face ID screen
  showPasswordForm = false;
  // true when the password form is revealed without a registered Face ID → suggest enabling it
  showFaceIdSuggestion = false;
  // true when the user opted to set up Face ID during this login → register right after auth
  wantsFaceIdSetup = false;
  // true while the biometric registration is in flight (after login, before navigate)
  registeringFaceId = false;
  phase: 'form' | 'entering' | 'success' = 'form';
  mfaPending = false;
  mfaToken   = '';
  digits: string[] = ['', '', '', '', '', ''];
  readonly appVersion = environment.version;

  // ── Three.js ──────────────────────────────────────────────
  private THREE: any;
  private renderer: any;
  private scene: any;
  private camera: any;
  private terrain: any;
  private terrainGeo: any;
  private terrainBase: Float32Array = new Float32Array();
  private terrainMat: any;
  private ico: any;
  private icoMat: any;
  private icoDotsMat: any;
  private icoCoreMat: any;
  private particles: any;
  private partMat: any;
  private partVel: Float32Array = new Float32Array();

  private _focusTs: number[] = [0, 0, 0, 0, 0, 0];
  private _keydownHandled = false;

  private raf = 0;
  private lastT = 0;
  private energy = 0;
  private mouse = { x: 0, y: 0 };
  private ripples: { x: number; z: number; t0: number }[] = [];
  private lastRipple = 0;
  private warpT0: number | null = null;
  private cam = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
  private camTarget = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
  private alive = false;

  private themeSub = new Subscription();
  private currentTheme = 'dark';

  private static readonly SCENE_THEMES: Record<string, any> = {
    dark:  { fog: 0x07080c, coreFill: 0x07080c, opacity: 1, blending: 'additive' },
    light: { fog: 0x07080c, coreFill: 0x07080c, opacity: 1, blending: 'additive' },
  };

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private mfaService: MfaService,
    public webauthnService: WebauthnService,
    private router: Router,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    public themeService: ThemeService,
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      senha: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  onEnterClick(): void {
    if (this.hasFaceId) {
      this.loginWithFaceId();
    } else {
      this.showPasswordForm = true;
      this.showFaceIdSuggestion = this.webauthnService.isSupported();
    }
  }

  // Explicit "Entrar com Passkey" CTA — always available while WebAuthn is
  // supported. With a registered passkey it runs the biometric ceremony; with
  // none it reveals the password form and offers to register one after auth.
  onPasskeyClick(): void {
    if (this.hasFaceId) {
      this.loginWithFaceId();
      return;
    }
    this.showPasswordForm = true;
    // Arm the post-login registration so a passkey is created once the user
    // authenticates with email + password (+ MFA), matching the desired flow.
    this.showFaceIdSuggestion = true;
    this.wantsFaceIdSetup = true;
  }

  loginWithFaceId(): void {
    if (this.faceIdLoading || this.loading) return;
    this.faceIdLoading = true;

    this.webauthnService.authenticateDiscoverable().subscribe({
      next: (res: any) => {
        this.auth.completeLogin(res.data.accessToken, res.data.user);
        // Keep faceIdLoading = true so button stays hidden during warp animation
        this.warpT0 = performance.now();
        setTimeout(() => { if (this.alive) this.router.navigate([this.auth.getHomeRoute()]); }, 1450);
      },
      error: (err: any) => {
        this.faceIdLoading = false;
        const isAborted = err?.name === 'NotAllowedError' || err?.message?.includes('NotAllowedError');
        if (isAborted) {
          // No credentials or user cancelled — fall back to password form
          this.showPasswordForm = true;
          return;
        }
        // Credential no longer valid — clear flag so UI adapts
        if (err?.error?.message?.includes('não reconhecida') || err?.status === 400) {
          localStorage.removeItem('webauthn_registered');
          this.hasFaceId = false;
        }
        const msg = err?.error?.message || 'Autenticação biométrica falhou.';
        this.snackBar.open(msg, 'Fechar', { duration: 5000, panelClass: ['snackbar-error'] });
        this.showPasswordForm = true;
      },
    });
  }

  private registerFaceIdAndNavigate(): void {
    this.registeringFaceId = true;
    this.webauthnService.register('Face ID').subscribe({
      next: () => {
        localStorage.setItem('webauthn_registered', 'true');
        this.registeringFaceId = false;
        if (!this.alive) return;
        this.warpT0 = performance.now();
        setTimeout(() => { if (this.alive) this.router.navigate([this.auth.getHomeRoute()]); }, 1450);
      },
      error: () => {
        // Falhou ou cancelou — navega de qualquer forma sem Face ID
        this.registeringFaceId = false;
        if (!this.alive) return;
        this.warpT0 = performance.now();
        setTimeout(() => { if (this.alive) this.router.navigate([this.auth.getHomeRoute()]); }, 1450);
      },
    });
  }

  ngOnInit(): void {
    this.themeService.applyThemeTransient('dark');
    if (this.auth.isLoggedIn()) {
      this.router.navigate([this.auth.getHomeRoute()]);
    }
    this.hasFaceId = this.webauthnService.isSupported() && !!localStorage.getItem('webauthn_registered');
  }

  ngAfterViewInit(): void {
    this.alive = true;
    this.themeSub = this.themeService.theme$.subscribe(t => {
      this.currentTheme = t;
      this.applySceneTheme();
    });
    this.waitForThree();
  }

  ngOnDestroy(): void {
    // Restore the user's saved theme when leaving login (e.g. a light-mode user).
    this.themeService.restoreStoredTheme();
    this.alive = false;
    cancelAnimationFrame(this.raf);
    this.themeSub.unsubscribe();
    window.removeEventListener('resize', this.onResize);
    if (this.renderer) {
      this.renderer.dispose();
      const el = this.renderer.domElement as HTMLCanvasElement;
      el.parentNode?.removeChild(el);
    }
  }

  // ── Scene bootstrap ───────────────────────────────────────

  private waitForThree(): void {
    if (!this.alive) return;
    const T = (window as any).THREE;
    if (T && this.canvasWrapper?.nativeElement) {
      this.THREE = T;
      this.ngZone.runOutsideAngular(() => this.initScene());
    } else {
      setTimeout(() => this.waitForThree(), 60);
    }
  }

  private initScene(): void {
    const THREE = this.THREE;
    const holder = this.canvasWrapper.nativeElement;
    try {
      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch { return; }

    const w = holder.clientWidth  || window.innerWidth;
    const h = holder.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h);
    holder.appendChild(this.renderer.domElement);

    this.scene  = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x07080c, 0.026);
    this.camera = new THREE.PerspectiveCamera(60, w / h, 0.1, 220);
    this.camera.position.set(0, 6.5, 26);

    const accent = new THREE.Color('#F79520');

    const geo = new THREE.PlaneGeometry(170, 100, 112, 62);
    geo.rotateX(-Math.PI / 2);
    this.terrainGeo  = geo;
    this.terrainBase = new Float32Array(geo.attributes.position.array);
    this.terrainMat  = new THREE.PointsMaterial({ color: accent, size: 0.16, transparent: true, opacity: 0.75, sizeAttenuation: true });
    this.terrain = new THREE.Points(geo, this.terrainMat);
    this.terrain.position.y = -4.5;
    this.scene.add(this.terrain);

    const icoGeo = new THREE.IcosahedronGeometry(3.6, 1);
    this.icoMat     = new THREE.MeshBasicMaterial({ color: accent, wireframe: true, transparent: true, opacity: 0.5 });
    this.ico        = new THREE.Mesh(icoGeo, this.icoMat);
    this.ico.position.set(0, 6, -9);
    this.scene.add(this.ico);
    this.icoCoreMat = new THREE.MeshBasicMaterial({ color: 0x07080c });
    this.ico.add(new THREE.Mesh(new THREE.IcosahedronGeometry(3.45, 1), this.icoCoreMat));
    this.icoDotsMat = new THREE.PointsMaterial({ color: accent, size: 0.18, transparent: true, opacity: 0.9 });
    this.ico.add(new THREE.Points(icoGeo, this.icoDotsMat));

    this.rebuildParticles();
    this.applySceneTheme();

    window.addEventListener('resize', this.onResize);
    this.raf = requestAnimationFrame(this.animate);
  }

  private rebuildParticles(): void {
    if (!this.THREE || !this.scene) return;
    const THREE = this.THREE;
    if (this.particles) { this.scene.remove(this.particles); this.particles.geometry.dispose(); }
    const count = 500;
    const pos = new Float32Array(count * 3);
    this.partVel = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3]     = (Math.random() - 0.5) * 130;
      pos[i * 3 + 1] = Math.random() * 34 - 4;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 90 - 10;
      this.partVel[i] = 0.3 + Math.random() * 1.1;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    this.partMat = new THREE.PointsMaterial({
      color: new THREE.Color('#F79520'), size: 0.22, transparent: true,
      opacity: 0.55, blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.particles = new THREE.Points(g, this.partMat);
    this.scene.add(this.particles);
  }

  private applySceneTheme(): void {
    if (!this.scene || !this.THREE) return;
    const T   = LoginComponent.SCENE_THEMES['dark'];
    const c   = new this.THREE.Color('#F79520');
    this.scene.fog.color.setHex(T.fog);
    this.icoCoreMat.color.setHex(T.coreFill);
    this.terrainMat.color.copy(c);
    this.terrainMat.opacity = 0.75;
    this.icoMat.color.copy(c);
    this.icoDotsMat.color.copy(c);
    this.partMat.color.copy(c);
    this.partMat.blending = this.THREE.AdditiveBlending;
    this.partMat.opacity  = 0.55;
    this.partMat.needsUpdate = true;
  }

  // ── Resize ────────────────────────────────────────────────

  private onResize = (): void => {
    if (!this.renderer || !this.canvasWrapper?.nativeElement) return;
    const { clientWidth: w, clientHeight: h } = this.canvasWrapper.nativeElement;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  // ── Animation loop ────────────────────────────────────────

  private animate = (): void => {
    if (!this.alive) return;
    this.raf = requestAnimationFrame(this.animate);
    const now = performance.now();
    const t   = now / 1000;
    const dt  = Math.min(0.05, t - (this.lastT || t));
    this.lastT = t;

    this.energy *= 0.94;
    this.ripples = this.ripples.filter(r => t - r.t0 < 3);

    const pos  = this.terrainGeo.attributes.position;
    const base = this.terrainBase;
    for (let i = 0; i < pos.count; i++) {
      const x = base[i * 3], z = base[i * 3 + 2];
      let y = Math.sin(x * 0.14 + t * 0.7) * 1.1
            + Math.cos(z * 0.18 + t * 0.5) * 0.9
            + Math.sin((x + z) * 0.07 + t * 0.32) * 0.6;
      for (const r of this.ripples) {
        const age = t - r.t0;
        const dx  = x - r.x, dz = z - r.z;
        const d   = Math.sqrt(dx * dx + dz * dz);
        y += Math.sin(d * 1.3 - age * 10) * 1.8 * Math.exp(-d * 0.16 - age * 1.6);
      }
      pos.setY(i, y);
    }
    pos.needsUpdate = true;

    this.ico.rotation.y += dt * (0.25 + this.energy * 1.4);
    this.ico.rotation.x += dt * (0.08 + this.energy * 0.5);
    const s = 1 + this.energy * 0.06;
    this.ico.scale.set(s, s, s);
    this.ico.position.y = 6 + Math.sin(t * 0.6) * 0.5;

    const pp = this.particles.geometry.attributes.position;
    for (let i = 0; i < pp.count; i++) {
      let y = pp.getY(i) + this.partVel[i] * dt * (1 + this.energy);
      if (y > 32) y = -4;
      pp.setY(i, y);
    }
    pp.needsUpdate = true;

    if (this.warpT0 !== null) {
      const k = Math.min(1, (now - this.warpT0) / 1400);
      const e = k * k * k;
      this.camera.position.set(this.cam.x, this.cam.y, this.cam.z - e * (this.cam.z + 12));
      this.camera.fov = this.cam.fov + e * 42;
      this.camera.updateProjectionMatrix();
      if (this.flashEl?.nativeElement) {
        this.flashEl.nativeElement.style.transition = 'none';
        this.flashEl.nativeElement.style.opacity    = String(Math.max(0, (k - 0.5) / 0.5));
      }
    } else {
      const ct = this.camTarget;
      this.cam.x    += (ct.x + this.mouse.x * 3.2 - this.cam.x) * 0.045;
      this.cam.y    += (ct.y - this.mouse.y * 1.8  - this.cam.y) * 0.045;
      this.cam.z    += (ct.z - this.cam.z) * 0.035;
      this.cam.fov  += (ct.fov - this.cam.fov) * 0.035;
      this.cam.lookY += (ct.lookY - this.cam.lookY) * 0.05;
      this.camera.position.set(this.cam.x, this.cam.y, this.cam.z);
      this.camera.fov = this.cam.fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.lookAt(0, this.cam.lookY, -4);
    this.renderer.render(this.scene, this.camera);
  };

  // ── Event handlers ────────────────────────────────────────

  onMouseMove(e: MouseEvent): void {
    const nx = (e.clientX / window.innerWidth) * 2 - 1;
    const ny = (e.clientY / window.innerHeight) * 2 - 1;
    this.mouse = { x: nx, y: ny };
    if (this.cardEl?.nativeElement && !this.warpT0) {
      this.cardEl.nativeElement.style.transform =
        `rotateY(${(nx * 6).toFixed(2)}deg) rotateX(${(-ny * 5).toFixed(2)}deg)`;
    }
  }

  onType(): void {
    this.energy = Math.min(3, this.energy + 0.5);
    const t = performance.now();
    if (t - this.lastRipple > 90) {
      this.lastRipple = t;
      this.ripples.push({ x: (Math.random() - 0.5) * 30, z: (Math.random() - 0.5) * 24, t0: t / 1000 });
    }
  }

  onSubmit(): void {
    if (this.mfaPending) {
      this.submitMfa();
      return;
    }
    if (this.loginForm.invalid || this.loading) return;
    this.loading = true;
    this.phase   = 'entering';

    const card = this.cardEl?.nativeElement;
    if (card) {
      card.style.transition = 'transform 0.9s cubic-bezier(0.5,0,1,1), opacity 0.8s ease';
      card.style.transform  = 'translateZ(380px) rotateX(6deg)';
      card.style.opacity    = '0';
    }
    this.camTarget = { x: 0, y: 6.2, z: 16, fov: 66, lookY: 6 };
    this.ripples.push({ x: 0, z: 0, t0: performance.now() / 1000 });

    const { email, senha } = this.loginForm.value;
    this.auth.login(email, senha).subscribe({
      next: (res: any) => {
        if (res.data?.mfaPending) {
          this.loading    = false;
          this.phase      = 'form';
          this.mfaPending = true;
          this.mfaToken   = res.data.mfaToken;
          this.camTarget  = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
          if (card) {
            card.style.transition = 'transform 0.5s ease, opacity 0.5s ease';
            card.style.transform  = '';
            card.style.opacity    = '1';
          }
          setTimeout(() => this.focusMfaFirst(), 180);
          return;
        }
        // Login success
        this.auth.completeLogin(res.data.accessToken, res.data.user);
        if (this.wantsFaceIdSetup && this.webauthnService.isSupported()) {
          this.registerFaceIdAndNavigate();
        } else {
          // Warp → dashboard immediately on success
          if (!this.alive) return;
          this.warpT0 = performance.now();
          setTimeout(() => { if (this.alive) this.router.navigate([this.auth.getHomeRoute()]); }, 1450);
        }
      },
      error: (err: any) => {
        this.loading = false;
        this.phase   = 'form';
        this.camTarget = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
        if (card) {
          card.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
          card.style.transform  = '';
          card.style.opacity    = '1';
        }
        const msg = err?.error?.message || this.translate.instant('auth.login_error');
        this.snackBar.open(msg, 'Fechar', { duration: 5000, panelClass: ['snackbar-error'] });
      },
    });
  }

  // ── MFA inline ────────────────────────────────────────────

  private submitMfa(): void {
    const token = this.digits.join('');
    if (token.length < 6 || this.loading) return;
    this.loading = true;

    const card = this.cardEl?.nativeElement;
    if (card) {
      card.style.transition = 'transform 0.9s cubic-bezier(0.5,0,1,1), opacity 0.8s ease';
      card.style.transform  = 'translateZ(380px) rotateX(6deg)';
      card.style.opacity    = '0';
    }
    this.camTarget = { x: 0, y: 6.2, z: 16, fov: 66, lookY: 6 };

    this.mfaService.verify(token, this.mfaToken).subscribe({
      next: (res: any) => {
        this.auth.completeLogin(res.data.accessToken, res.data.user);
        this.loading = false;
        if (this.wantsFaceIdSetup && this.webauthnService.isSupported()) {
          this.registerFaceIdAndNavigate();
        } else {
          this.warpT0  = performance.now();
          setTimeout(() => { if (this.alive) this.router.navigate([this.auth.getHomeRoute()]); }, 1450);
        }
      },
      error: (err: any) => {
        this.loading = false;
        // Restore the card that submitMfa animated out — without this the screen stays
        // "frozen" (card warped away / invisible) and the user can't retry or see the error.
        this.camTarget = { x: 0, y: 6.5, z: 26, fov: 60, lookY: 3.8 };
        if (card) {
          card.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
          card.style.transform  = '';
          card.style.opacity    = '1';
        }
        this.digits  = ['', '', '', '', '', ''];
        this.otpInputs?.toArray().forEach(el => (el.nativeElement.value = ''));
        this.focusMfaFirst();
        this.snackBar.open(err?.error?.message || 'Código inválido. Tente novamente.', 'Fechar', {
          duration: 4000, panelClass: ['snackbar-error'],
        });
      },
    });
  }

  private focusMfaFirst(): void {
    const inputs = this.otpInputs?.toArray();
    if (inputs?.length) {
      inputs[0].nativeElement.focus();
      this._focusTs[0] = 0;
    }
  }

  private advanceMfa(i: number): void {
    if (i >= 5) return;
    setTimeout(() => {
      const next = this.otpInputs.toArray()[i + 1].nativeElement;
      next.value = '';
      this._focusTs[i + 1] = Date.now();
      next.focus();
    }, 0);
  }

  onMfaFocus(_e: Event, i: number): void {
    this._focusTs[i] = Date.now();
  }

  onMfaDigit(e: Event, i: number): void {
    const input = e.target as HTMLInputElement;

    if (this._keydownHandled) {
      input.value = this.digits[i] || '';
      return;
    }

    if (Date.now() - this._focusTs[i] < 30) {
      input.value = '';
      return;
    }

    const val = input.value.replace(/\D/g, '');

    if (val.length > 1) {
      const inputs = this.otpInputs.toArray();
      val.slice(0, 6 - i).split('').forEach((ch, j) => {
        inputs[i + j].nativeElement.value = ch;
        this.digits[i + j] = ch;
      });
      inputs[Math.min(i + val.length, 5)].nativeElement.focus();
      if (this.digits.join('').length === 6) setTimeout(() => this.submitMfa(), 60);
      return;
    }

    input.value = val ? val[0] : '';
    this.digits[i] = input.value;
    this.energy = Math.min(3, this.energy + 0.4);
    if (this.digits[i]) this.advanceMfa(i);
    if (this.digits.join('').length === 6) setTimeout(() => this.submitMfa(), 60);
  }

  onMfaKey(e: KeyboardEvent, i: number): void {
    if (/^\d$/.test(e.key)) {
      e.preventDefault();
      this._keydownHandled = true;
      const input = this.otpInputs.toArray()[i].nativeElement;
      input.value = e.key;
      this.digits[i] = e.key;
      this.energy = Math.min(3, this.energy + 0.4);
      this.advanceMfa(i);
      if (this.digits.join('').length === 6) setTimeout(() => this.submitMfa(), 60);
      setTimeout(() => { this._keydownHandled = false; }, 0);
      return;
    }
    if (e.key === 'Backspace') {
      e.preventDefault();
      const input = this.otpInputs.toArray()[i].nativeElement;
      if (this.digits[i]) {
        input.value = '';
        this.digits[i] = '';
      } else if (i > 0) {
        this.digits[i - 1] = '';
        const prev = this.otpInputs.toArray()[i - 1].nativeElement;
        prev.value = '';
        prev.focus();
      }
      return;
    }
    if (e.key === 'ArrowLeft'  && i > 0) this.otpInputs.toArray()[i - 1].nativeElement.focus();
    if (e.key === 'ArrowRight' && i < 5) this.otpInputs.toArray()[i + 1].nativeElement.focus();
  }

  onMfaPaste(e: ClipboardEvent): void {
    e.preventDefault();
    const text = e.clipboardData?.getData('text')?.replace(/\D/g, '').slice(0, 6) || '';
    if (!text) return;
    const inputs = this.otpInputs.toArray();
    text.split('').forEach((ch, i) => {
      if (inputs[i]) {
        inputs[i].nativeElement.value = ch;
        this.digits[i] = ch;
      }
    });
    const focus = Math.min(text.length, 5);
    inputs[focus].nativeElement.focus();
    if (text.length === 6) setTimeout(() => this.submitMfa(), 60);
  }
}
